import {
  Alert,
  Button,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import Iconify from '../Iconify';
import Scrollbar from '../Scrollbar';
import { baseUrl } from '../../constant/config';
import { PLATFORM_META, STATUS_COLOR, formatDate } from './shareMeta';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

const STATUSES = [
  { value: '', label: 'All statuses' },
  { value: 'published', label: 'Published' },
  { value: 'deleted', label: 'Deleted' },
  { value: 'failed', label: 'Failed' },
];

const truncate = (text, max = 90) =>
  !text ? '' : text.length > max ? `${text.slice(0, max)}...` : text;

/**
 * This app's own record of every publish attempt.
 *
 * Unlike the live tabs, this includes attempts that never became a post -
 * Meta has no memory of a publish that failed, so a failure is only ever
 * visible here.
 */
export default function ShareHistoryPanel({ enabled, onEdit, onDelete, onToast }) {
  const queryClient = useQueryClient();

  const [platform, setPlatform] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading, isError, error, isFetching, refetch } = useQuery(
    ['platformShares', platform, status, debouncedSearch, page],
    async () => {
      const params = new URLSearchParams({ limit: PAGE_SIZE, skip: page * PAGE_SIZE });
      if (platform) params.append('platform', platform);
      if (status) params.append('status', status);
      if (debouncedSearch) params.append('q', debouncedSearch);
      const { data } = await axios.get(`${baseUrl}/api/videos/shares?${params.toString()}`);
      return data.data;
    },
    { enabled, refetchOnWindowFocus: false, keepPreviousData: true }
  );

  const loading = isLoading || !enabled;
  const rows = useMemo(() => data?.rows || [], [data]);
  const counts = data?.counts || {};
  const total = data?.total || 0;

  // Ask Meta what this post looks like now. Also catches posts deleted from
  // inside the Facebook or Instagram app - the server marks those deleted.
  const { mutate: checkLive, isLoading: checking, variables: checkingRow } = useMutation(
    async (share) => {
      const { data } = await axios.get(`${baseUrl}/api/videos/shares/${share._id}/live`);
      return data.data;
    },
    {
      onSuccess: ({ live }) => {
        queryClient.invalidateQueries('platformShares');
        onToast(
          live.exists
            ? `Live on the platform${
                typeof live.likeCount === 'number' ? ` - ${live.likeCount} likes` : ''
              }`
            : 'That post is no longer on the platform - marked deleted'
        );
      },
      onError: (err) => onToast(err?.response?.data?.message || err.message),
    }
  );

  const resetFilters = () => {
    setPlatform('');
    setStatus('');
    setSearch('');
    setPage(0);
  };

  const hasFilters = Boolean(platform || status || debouncedSearch);
  const asTarget = (row) => ({
    mode: 'record',
    platform: row.platform,
    shareId: row._id,
    postId: row.postId,
    status: row.status,
    caption: row.caption,
    title: row.debateTitle,
    permalink: row.permalink,
    createdTime: row.sharedAt,
    fromDebate: true,
  });

  return (
    <Card>
      <Stack direction="row" spacing={1} sx={{ p: 2, pb: 0 }} flexWrap="wrap" useFlexGap>
        <Chip label={`${counts.published || 0} published`} color="success" variant="outlined" />
        <Chip label={`${counts.deleted || 0} deleted`} variant="outlined" />
        <Chip label={`${counts.failed || 0} failed`} color="error" variant="outlined" />
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ p: 2 }}
        alignItems={{ sm: 'center' }}
      >
        <TextField
          size="small"
          placeholder="Search captions"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flexGrow: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Platform"
          value={platform}
          onChange={(e) => {
            setPlatform(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All platforms</MenuItem>
          <MenuItem value="facebook">Facebook</MenuItem>
          <MenuItem value="instagram">Instagram</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 150 }}
        >
          {STATUSES.map((s) => (
            <MenuItem key={s.value} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </TextField>
        {hasFilters && (
          <Button size="small" onClick={resetFilters}>
            Clear
          </Button>
        )}
      </Stack>

      <Scrollbar>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 900 }} aria-label="publish history">
            <TableHead>
              <TableRow>
                <TableCell>Platform</TableCell>
                <TableCell>Debate</TableCell>
                <TableCell>Caption</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell>Shared</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading &&
                Array.from({ length: 5 }).map((_, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={i}>
                    <TableCell colSpan={6}>
                      <Skeleton height={32} />
                    </TableCell>
                  </TableRow>
                ))}

              {isError && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 4 }}>
                    <Alert
                      severity="error"
                      action={
                        <Button color="inherit" size="small" onClick={() => refetch()}>
                          Retry
                        </Button>
                      }
                    >
                      Could not load the history: {error?.response?.data?.message || error?.message}
                    </Alert>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                !isError &&
                rows.map((row) => {
                  const meta = PLATFORM_META[row.platform] || {};
                  const canEdit = meta.canEditCaption && row.status === 'published';
                  return (
                    <TableRow key={row._id} hover>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {meta.icon}
                          <Typography variant="body2">{meta.label || row.platform}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell
                        className="one_line_text"
                        style={{ minWidth: '150px', maxWidth: '200px' }}
                        title={row.debateTitle}
                      >
                        {row.debateTitle || <span style={{ opacity: 0.5 }}>Debate removed</span>}
                      </TableCell>
                      <TableCell style={{ minWidth: '220px', maxWidth: '320px' }}>
                        <Tooltip title={row.caption || ''}>
                          <Typography variant="body2">
                            {truncate(row.caption) || <span style={{ opacity: 0.4 }}>-</span>}
                          </Typography>
                        </Tooltip>
                        {row.status === 'failed' && row.error && (
                          <Typography variant="caption" color="error">
                            {truncate(row.error, 70)}
                          </Typography>
                        )}
                        {row.captionUpdatedAt && (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            edited {formatDate(row.captionUpdatedAt)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={row.status}
                          color={STATUS_COLOR[row.status] || 'default'}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell style={{ minWidth: '150px' }}>
                        <Typography variant="body2">{formatDate(row.sharedAt)}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Check the live post on Meta">
                          <span>
                            <IconButton
                              size="small"
                              disabled={!row.postId || (checking && checkingRow?._id === row._id)}
                              onClick={() => checkLive(row)}
                            >
                              {checking && checkingRow?._id === row._id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <RefreshIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            canEdit
                              ? 'Edit caption'
                              : row.platform === 'instagram'
                              ? 'Instagram captions cannot be edited after publishing'
                              : 'Only a published post can be edited'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={!canEdit}
                              onClick={() => onEdit(asTarget(row))}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Open post">
                          <span>
                            <IconButton
                              size="small"
                              disabled={!row.permalink}
                              component="a"
                              href={row.permalink || undefined}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onDelete(asTarget(row))}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}

              {!loading && !isError && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      {page > 0 ? 'No more attempts' : 'Nothing published from here yet'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {hasFilters
                        ? 'No attempt matches these filters.'
                        : 'Publish a recording from the Recordings page and it will be recorded here.'}
                    </Typography>
                    {hasFilters && (
                      <Button size="small" onClick={resetFilters} sx={{ mt: 1 }}>
                        Clear filters
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>

      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ p: 2 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>
          {total > 0
            ? `${page * PAGE_SIZE + 1}-${Math.min((page + 1) * PAGE_SIZE, total)} of ${total}`
            : '0 of 0'}
        </Typography>
        <Button
          size="small"
          disabled={page === 0 || isFetching}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
        >
          Previous
        </Button>
        <Button
          size="small"
          disabled={(page + 1) * PAGE_SIZE >= total || isFetching}
          onClick={() => setPage((p) => p + 1)}
          endIcon={<Iconify icon="eva:arrow-ios-forward-fill" />}
        >
          Next
        </Button>
      </Stack>
    </Card>
  );
}

ShareHistoryPanel.propTypes = {
  enabled: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onToast: PropTypes.func.isRequired,
};
